// @acme/api-client: fetch wrapper shared by every app. Adds the bearer token, a per-request
// X-Correlation-Id (traced by the backend into logs and audit), JSON handling, AbortSignal
// support and ProblemDetails (RFC 9457) → ApiProblem.

export const CORRELATION_HEADER = 'X-Correlation-Id'

export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  correlationId?: string
  code?: string
  errors?: Record<string, string[]>
}

export class ApiProblem extends Error {
  readonly status: number
  readonly title: string
  readonly code: string | undefined
  readonly correlationId: string | undefined
  // The ProblemDetails "detail" (e.g. what the database said about a Monitoring query).
  readonly detail: string | undefined
  // Field errors keyed by the backend property name (e.g. "Role", "Owner.Email").
  readonly errors: Record<string, string[]>

  constructor(status: number, problem: ProblemDetails, correlationId?: string) {
    super(problem.title ?? `HTTP ${status}`)
    this.name = 'ApiProblem'
    this.status = status
    this.title = problem.title ?? `HTTP ${status}`
    this.code = problem.code
    this.correlationId = problem.correlationId ?? correlationId
    this.detail = problem.detail
    this.errors = problem.errors ?? {}
  }

  get isValidation(): boolean {
    return this.status === 400 && Object.keys(this.errors).length > 0
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isConflict(): boolean {
    return this.status === 409
  }

  // First error of a field, matching the backend name case-insensitively (FluentValidation uses
  // PascalCase, MVC model binding uses "$.camelCase").
  fieldError(field: string): string | undefined {
    const wanted = field.toLowerCase()
    const entry = Object.entries(this.errors).find(([key]) => key.replace(/^\$\./, '').toLowerCase() === wanted)
    return entry?.[1][0]
  }
}

// The network failed before any HTTP response (offline, DNS, CORS, backend down).
export class NetworkError extends Error {
  constructor(readonly correlationId: string, cause: unknown) {
    super('Network request failed', { cause })
    this.name = 'NetworkError'
  }
}

export interface ApiClientOptions {
  baseUrl: string
  // Current access token, or null when signed out. Called on every request (tokens rotate).
  getAccessToken: () => Promise<string | null> | string | null
  // Called on 401, e.g. to start a new sign-in.
  onUnauthorized?: () => void
  fetch?: typeof fetch
}

export interface RequestOptions {
  query?: Record<string, string | number | boolean | null | undefined>
  body?: unknown
  signal?: AbortSignal
  headers?: Record<string, string>
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export function newCorrelationId(): string {
  return crypto.randomUUID().replaceAll('-', '')
}

// One Server-Sent Event: the "event:" name (default "message") and the parsed JSON "data:".
export interface StreamEvent {
  event: string
  data: unknown
}

export interface StreamOptions {
  signal?: AbortSignal
  onEvent: (event: StreamEvent) => void
}

export interface ApiClient {
  request<T>(method: HttpMethod, path: string, options?: RequestOptions): Promise<T>
  // POST that answers text/event-stream: calls onEvent for each event until the stream ends.
  // Errors before the stream (4xx/5xx) throw ApiProblem like request().
  stream(path: string, body: unknown, options: StreamOptions): Promise<void>
  get<T>(path: string, options?: RequestOptions): Promise<T>
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  delete<T = void>(path: string, options?: RequestOptions): Promise<T>
  // GET of a file (PDF, attachment): the bytes and the name from Content-Disposition, with the same token and errors.
  download(path: string, options?: RequestOptions): Promise<DownloadedFile>
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const fetchImpl = options.fetch ?? ((input, init) => globalThis.fetch(input, init))
  const baseUrl = options.baseUrl.replace(/\/+$/, '')

  // Headers, token, fetch and the error mapping shared by request() and stream().
  async function send(method: HttpMethod, path: string, requestOptions: RequestOptions, accept: string): Promise<Response> {
    const correlationId = newCorrelationId()
    const headers: Record<string, string> = {
      Accept: accept,
      [CORRELATION_HEADER]: correlationId,
      ...requestOptions.headers,
    }

    const token = await options.getAccessToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    // FormData goes as multipart (the browser sets the boundary); anything else as JSON.
    let body: string | FormData | undefined
    if (requestOptions.body instanceof FormData) {
      body = requestOptions.body
    } else if (requestOptions.body !== undefined) {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify(requestOptions.body)
    }

    let response: Response
    try {
      response = await fetchImpl(buildUrl(baseUrl, path, requestOptions.query), {
        method,
        headers,
        body,
        signal: requestOptions.signal,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error
      }
      throw new NetworkError(correlationId, error)
    }

    if (response.status === 401) {
      options.onUnauthorized?.()
    }

    if (!response.ok) {
      throw new ApiProblem(response.status, await readProblem(response), response.headers.get(CORRELATION_HEADER) ?? correlationId)
    }

    return response
  }

  async function request<T>(method: HttpMethod, path: string, requestOptions: RequestOptions = {}): Promise<T> {
    const response = await send(method, path, requestOptions, 'application/json')
    if (response.status === 204) {
      return undefined as T
    }

    const text = await response.text()
    return (text ? JSON.parse(text) : undefined) as T
  }

  async function stream(path: string, body: unknown, streamOptions: StreamOptions): Promise<void> {
    const response = await send('POST', path, { body, signal: streamOptions.signal }, 'text/event-stream')
    if (!response.body) return

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    const flush = (block: string) => {
      let event = 'message'
      const data: string[] = []
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim()
        else if (line.startsWith('data:')) data.push(line.slice(5).trimStart())
      }
      if (!data.length) return
      const raw = data.join('\n')
      let parsed: unknown = raw
      try {
        parsed = JSON.parse(raw)
      } catch {
        // Not JSON: passed as text.
      }
      streamOptions.onEvent({ event, data: parsed })
    }

    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n')
      let index: number
      while ((index = buffer.indexOf('\n\n')) >= 0) {
        flush(buffer.slice(0, index))
        buffer = buffer.slice(index + 2)
      }
    }
    buffer += decoder.decode()
    if (buffer.trim()) flush(buffer)
  }

  return {
    request,
    stream,
    get: (path, requestOptions) => request('GET', path, requestOptions),
    post: (path, body, requestOptions) => request('POST', path, { ...requestOptions, body }),
    put: (path, body, requestOptions) => request('PUT', path, { ...requestOptions, body }),
    delete: (path, requestOptions) => request('DELETE', path, requestOptions),
    download: async (path, requestOptions = {}) => {
      const response = await send('GET', path, requestOptions, '*/*')
      return { blob: await response.blob(), fileName: fileNameOf(response.headers.get('Content-Disposition')) }
    },
  }
}

export interface DownloadedFile {
  blob: Blob
  fileName: string | null
}

// filename*=UTF-8''… wins over filename="…" (RFC 6266).
export function fileNameOf(disposition: string | null): string | null {
  if (!disposition) return null
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim())
    } catch {
      // Falls back to the plain name.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain?.[1]?.trim() ?? null
}

export function buildUrl(baseUrl: string, path: string, query?: RequestOptions['query']): string {
  const url = `${baseUrl}/${path.replace(/^\/+/, '')}`
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value))
    }
  }

  const search = params.toString()
  return search ? `${url}?${search}` : url
}

async function readProblem(response: Response): Promise<ProblemDetails> {
  try {
    const text = await response.text()
    return text ? (JSON.parse(text) as ProblemDetails) : {}
  } catch {
    return {}
  }
}

// Paging contract of the backend (PagedRequest / PagedResponse<T>).
export interface PagedResponse<T> {
  items: T[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}
